import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import React from 'react';

import { type Option } from '../models/IRadioBtnOption';

interface Props<T extends Record<string, Option>> {
  options: T;
  value: keyof T | null;
  onChange: (key: keyof T, value: T[keyof T]['value']) => void;
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean;
  error?: boolean;
  helperText?: string[];
}

export default function CRadioGroup<T extends Record<string, Option>>(props: Props<T>) {
  const { options, value, onChange, direction = 'vertical', disabled = false, error, helperText } = props;

  return (
    <div className={'flex flex-col gap-2'}>
      <ul
        style={{ flexDirection: direction === 'vertical' ? 'column' : 'row' }}
        className={'flex gap-4 list-none p-0 m-0'}
      >
        {(Object.entries(options) as Array<[keyof T, Option]>).map(([key, option]) => {
          const selected = key === value;

          return (
            <li
              key={key as string}
              className={'cursor-pointer select-none duration-200 hover:opacity-60'}
              style={{
                cursor: disabled ? 'default' : 'pointer',
                pointerEvents: disabled ? 'none' : 'auto'
              }}
              onClick={() => {
                if (!disabled) onChange(key, option.value);
              }}
            >
              <div
                className={'flex gap-2'}
                style={{
                  opacity: disabled ? 0.4 : '1'
                }}
              >
                {selected ? (
                  <RadioButtonCheckedIcon color={disabled ? 'disabled' : 'primary'} sx={{ fontSize: '1.5rem' }} />
                ) : (
                  <RadioButtonUncheckedIcon color={disabled ? 'disabled' : 'inherit'} sx={{ fontSize: '1.5rem' }} />
                )}

                <div className={'flex flex-col'}>
                  <p>{option.label}</p>
                  {option.description && <p className={'text-sm'}>{option.description}</p>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {(helperText?.length ?? 0) > 0 && (
        <span
          className={'text-xs ml-8'}
          style={{
            color: error ? 'rgb(211, 47, 47)' : 'inherit'
          }}
        >
          {helperText?.join('\n')}
        </span>
      )}
    </div>
  );
}
